// src/data/quizQuestions.ts
export type Dimension =
  | 'systems' | 'regulatory' | 'sponsor' | 'technical' | 'scope';

export const DIMENSION_LABELS: Record<Dimension, string> = {
  systems: 'Systems foundation',
  regulatory: 'Regulatory posture',
  sponsor: 'Sponsor + successor',
  technical: 'Technical capacity',
  scope: 'Engagement scope',
};

export const DIMENSION_ORDER: Dimension[] = [
  'systems', 'regulatory', 'sponsor', 'technical', 'scope',
];

export interface QuizAnswer {
  label: string;
  score: 0 | 1 | 2 | 3;
}

export interface QuizQuestion {
  id: string;
  dimension: Dimension;
  question: string;
  answers: QuizAnswer[];
}

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  // Systems foundation, 2 probes
  {
    id: 'systems-1',
    dimension: 'systems',
    question: 'What do you use to run day-to-day operations?',
    answers: [
      { label: 'A platform built for our industry (ServiceTitan, Procore, Epic, Salesforce, NetSuite, Workday, etc.)', score: 3 },
      { label: 'A general-purpose business platform we adapted (QuickBooks, Dynamics, HubSpot, etc.)', score: 2 },
      { label: 'A mix of specialized tools stitched together manually', score: 1 },
      { label: 'No formal system, or mostly paper- and spreadsheet-based', score: 0 },
    ],
  },
  {
    id: 'systems-2',
    dimension: 'systems',
    question: 'How are software and technology decisions made today?',
    answers: [
      { label: 'Centrally, leadership owns tool selection and rollout', score: 3 },
      { label: 'Mostly central, with some team-level adoption', score: 2 },
      { label: 'Decentralized, teams pick their own tools', score: 1 },
      { label: 'Informal, we mostly use what we have always used', score: 0 },
    ],
  },
  // Regulatory posture, 2 probes
  {
    id: 'regulatory-1',
    dimension: 'regulatory',
    question: 'What regulatory or compliance requirements apply to your business?',
    answers: [
      { label: 'Heavy regulation (HIPAA, FDA, GLBA, SOC 2, ISO 27001, OSHA major, FAA, DOT, EPA, state contractor licensing with audits)', score: 3 },
      { label: 'Moderate (licensing, periodic audits, state-level data or safety requirements)', score: 2 },
      { label: 'Light (basic regulatory hygiene, no formal audits)', score: 1 },
      { label: 'Effectively unregulated', score: 0 },
    ],
  },
  {
    id: 'regulatory-2',
    dimension: 'regulatory',
    question: 'How structured is your compliance and operations posture today?',
    answers: [
      { label: 'Documented policies, named owners, regular reviews or audits', score: 3 },
      { label: 'Documented for some areas, gaps in others', score: 2 },
      { label: 'Informal, we know what applies but no formal program', score: 1 },
      { label: 'Have not assessed this yet', score: 0 },
    ],
  },
  // Sponsor + successor, 2 probes
  {
    id: 'sponsor-1',
    dimension: 'sponsor',
    question: 'Who would sponsor a Parsant engagement internally?',
    answers: [
      { label: 'Owner, CEO, or COO with explicit mandate and budget', score: 3 },
      { label: 'C-level or General Manager with informal mandate, will need to defend it up', score: 2 },
      { label: 'Department head or VP, will need to advocate to the executive team', score: 1 },
      { label: 'No clear sponsor identified yet', score: 0 },
    ],
  },
  {
    id: 'sponsor-2',
    dimension: 'sponsor',
    question: 'Could you name someone today who could be trained to own this long-term?',
    answers: [
      { label: 'Yes, a specific person already in the role', score: 3 },
      { label: 'Maybe, would need to develop or hire someone', score: 2 },
      { label: 'Unclear, depends on what the role looks like', score: 1 },
      { label: 'No, finding that owner is part of what we would hire Parsant to do', score: 0 },
    ],
  },
  // Technical capacity, 2 probes
  {
    id: 'technical-1',
    dimension: 'technical',
    question: 'What is your in-house technical capacity for software and AI?',
    answers: [
      { label: 'Developers on staff with AI or LLM experience', score: 3 },
      { label: 'IT or developers, no AI-specific experience yet', score: 2 },
      { label: 'IT support staff, with outsourced development as needed', score: 1 },
      { label: 'No technical staff, we rely on our software vendors', score: 0 },
    ],
  },
  {
    id: 'technical-2',
    dimension: 'technical',
    question: 'How would your team handle a critical software outage during business hours?',
    answers: [
      { label: 'Documented response process, multiple people who can troubleshoot in-house', score: 3 },
      { label: 'A couple of people could troubleshoot, partial process', score: 2 },
      { label: 'One person handles it, and they are sometimes on PTO', score: 1 },
      { label: 'We would call our software vendor\'s support line', score: 0 },
    ],
  },
  // Engagement scope, 2 probes
  {
    id: 'scope-1',
    dimension: 'scope',
    question: 'Where are you in this conversation today?',
    answers: [
      { label: 'Pre-engagement, need a scoping exercise (Plan)', score: 3 },
      { label: 'Scoping done, ready to embed an operator for 3 to 6 months (Embed)', score: 3 },
      { label: 'Mid-build, want continuity through delivery and hand-off', score: 2 },
      { label: 'System is shipped, need it documented and transitioned (Hand-off)', score: 3 },
    ],
  },
  {
    id: 'scope-2',
    dimension: 'scope',
    question: 'What is the realistic budget envelope for the next 12 months?',
    answers: [
      { label: '$75K to $210K, fits a full Plan / Embed / Hand-off sequence', score: 3 },
      { label: '$30K to $75K, fits Plan plus a partial Embed', score: 2 },
      { label: 'Below $30K, fits a single-mode engagement only', score: 1 },
      { label: 'Unknown, budget depends on what the engagement looks like', score: 0 },
    ],
  },
];
